import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ButtonComponent } from '../../shared/button/button.component';
import { ButtonSize, ButtonState, ButtonType, IInterview, IUser } from '../../../assets/types/dtoTypes';

export interface RescheduleInterviewData {
  interview: IInterview;
  candidate: IUser;
}

export interface RescheduleInterviewResult {
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  guests: { guestName: string; guestEmail: string; guestId: string }[];
}

@Component({
  selector: 'app-interview-reschedule-modal',
  templateUrl: './interview-reschedule-modal.component.html',
  styleUrls: ['./interview-reschedule-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ButtonComponent,
  ],
})
export class InterviewRescheduleModalComponent {
  data = inject<RescheduleInterviewData>(MAT_DIALOG_DATA);
  private dialogRef = inject<MatDialogRef<InterviewRescheduleModalComponent>>(MatDialogRef);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;

  title: string = this.data.interview.title;
  selectedDate: Date | null = null;
  startTime: string = '';
  endTime: string = '';
  minDate: Date = new Date();

  guestName: string = '';
  guestEmail: string = '';
  invitedGuests: { guestName: string; guestEmail: string; guestId: string }[] =
    this.data.interview.guests ? [...this.data.interview.guests] : [];

  get currentDateLabel(): string {
    return new Date(this.data.interview.date).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  }

  get currentTimeLabel(): string {
    return `${this.formatTime(this.data.interview.startTime)} → ${this.formatTime(this.data.interview.endTime)} (${this.getDuration()})`;
  }

  get candidateName(): string {
    return `${this.data.candidate?.firstname ?? ''} ${this.data.candidate?.lastname ?? ''}`.trim()
      || this.data.candidate?.username || '';
  }

  get isFormValid(): boolean {
    return !!this.selectedDate && !!this.startTime && !!this.endTime && this.isTimeRangeValid();
  }

  isTimeRangeValid(): boolean {
    if (!this.startTime || !this.endTime) return true;
    const [sh, sm] = this.startTime.split(':').map(Number);
    const [eh, em] = this.endTime.split(':').map(Number);
    return (eh * 60 + em) > (sh * 60 + sm);
  }

  addGuest(): void {
    if (!this.guestName.trim() || !this.isValidEmail(this.guestEmail)) return;
    this.invitedGuests.push({ guestName: this.guestName.trim(), guestEmail: this.guestEmail.trim(), guestId: '' });
    this.guestName = '';
    this.guestEmail = '';
  }

  removeGuest(index: number): void {
    this.invitedGuests.splice(index, 1);
  }

  getInitials(name: string): string {
    return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  confirm(): void {
    if (!this.isFormValid) return;
    const d = this.selectedDate!;
    const pad = (n: number) => String(n).padStart(2, '0');
    const dateStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const toHms = (t: string) => {
      const [h = '00', m = '00'] = t.split(':');
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:00`;
    };
    this.dialogRef.close({
      title: this.title.trim() || this.data.interview.title,
      date: dateStr,
      startTime: toHms(this.startTime),
      endTime: toHms(this.endTime),
      guests: this.invitedGuests,
    } as RescheduleInterviewResult);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private formatTime(time: string): string {
    const [h, m] = time.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  }

  private getDuration(): string {
    const [sh, sm] = this.data.interview.startTime.split(':').map(Number);
    const [eh, em] = this.data.interview.endTime.split(':').map(Number);
    return `${(eh * 60 + em) - (sh * 60 + sm)}m`;
  }
}
